<script lang="ts">
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import TextImageSidesStructure, {
		ItemDescription,
		ItemNativeName,
		ItemTags,
		ItemTitle,
		ItemViewPrimary
	} from '$lib/components/city/features/TextImageSidesStructure.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import KeepExploringSection from '$lib/components/ui/section/KeepExploringSection.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import { CITY_FEATURE_TYPE_COLOURS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import type { NightlifeSpot } from '$lib/features/nightlifeSpot/nightlifeSpot.type';
	import { FULL_SITE_URL } from '$lib/statics';
	import {
		buildExampleItemsPhrase,
		closestByLength,
		findFirstAvailableImage,
		sortShortestToLongest
	} from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import IconStarCircle from '~icons/mdi/star-circle';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.NIGHTLIFE_SPOT;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const items = $derived<NightlifeSpot[]>(output.items);
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(items.map((item) => item.feature.englishName));
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: ImageDetails | undefined = findFirstAvailableImage(items);

		interface Limits {
			exampleItems: number;
		}
		function examplesLimits(limits: Limits): { exampleItems: string } {
			return {
				exampleItems: buildExampleItemsPhrase({
					names: orderedNames,
					totalItems: orderedNames.length,
					limit: limits.exampleItems
				})
			};
		}
		const examples = {
			large: examplesLimits({ exampleItems: 4 }),
			mid: examplesLimits({ exampleItems: 3 }),
			small: examplesLimits({ exampleItems: 2 })
		};
		const description: string = closestByLength([
			`Find out where the locals head after dark in ${location.city.label}, ${examples.large.exampleItems}.`,
			`Find out where the locals head after dark in ${location.city.label}, ${examples.mid.exampleItems}.`,
			`Find out where the locals head after dark in ${location.city.label}, ${examples.small.exampleItems}.`
		]);

		return {
			title: `${location.city.label} Nightlife & Evening Spots: ${shortestName} & More`,
			description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdNightlifeList = $derived.by(() => {
		const json = data.output.jsonLdNightlifeList;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdNightlifeList]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	{#if meta.imageUrl}
		<meta property="og:image" content={meta.imageUrl} />
		<meta property="twitter:image" content={meta.imageUrl} />
	{/if}
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<HeroNeutral
	title={`Nightlife & Evening Spots in ${location.city.label}`}
	description={`Skip the tourist traps and head straight to where the energy is. From bustling entertainment districts to laid-back neighborhood squares, discover the best areas to experience the city's evening culture.`}
	breadcrumbs={data.output.breadcrumbs}
	decorations={{
		coordinates: items.flatMap((item) => item.primary?.place?.coordinates ?? item.feature.place?.coordinates ?? []),
		hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
	}}
/>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<ul class="flex flex-col gap-12 lg:gap-16">
		{#each items as item, i (item.feature.id)}
			<li>
				{@render ItemRow(item, i === 0)}
			</li>
		{/each}
	</ul>

	<KeepExploringSection categories={output.keepExploring} {location} />
</div>

{#snippet ItemRow(item: NightlifeSpot, isFirstEager: boolean)}
	{const images = $derived<ImageDetails[]>(item.feature.images.length > 0 ? item.feature.images : item.primary?.images || [])}

	<article
		id={item.feature.slug}
		class="flex scroll-mt-28 flex-col gap-8 rounded-box border border-base-content/10 bg-base-100 p-6 shadow-sm transition-all hover:border-base-content/20 md:p-8"
	>
		<TextImageSidesStructure englishName={item.feature.englishName} {images} eagerLoadImages={isFirstEager}>
			{#snippet TextContentSnippet()}
				<div class="flex flex-col gap-1">
					{@render ItemTitle({ englishName: item.feature.englishName })}
					{@render ItemNativeName({
						nativeName: item.feature.nativeName,
						language: output.countryLanguage
					})}
				</div>
				{@render ItemDescription({ text: item.feature.description })}

				<div class="flex flex-col gap-1">
					<h3 class="mt-2 text-lg text-base-content">VIBE</h3>
					{@render ItemDescription({ text: item.vibe })}
				</div>

				<div class="mt-2 flex flex-col gap-6">
					{@render ItemTags({ heading: { text: 'BEST FOR', Icon: IconStarCircle }, tags: item.bestFor })}
					{@render ItemViewPrimary({ primary: item.primary, location })}
				</div>
			{/snippet}
		</TextImageSidesStructure>

		<div class="border-t border-base-content/10 pt-6">
			<PlaceExtras
				name={item.feature.englishName}
				place={item.primary?.place ?? item.feature.place}
				videos={item.feature.videos}
				typeMapPoints={output.typeMapPoints}
			/>
		</div>
	</article>
{/snippet}
